import { useState, useEffect, useRef } from 'react'
import {
  Activity,
  AlertCircle,
  CalendarClock,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  FileSignature,
  FlaskConical,
  Loader2,
  RefreshCw,
  Save,
  Stethoscope,
} from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'

import { consultationService, getAllConsultationsByPatient, type ConsultationSummary } from '@/services/consultation.service'
import { Cie10Combobox } from './Cie10Combobox'
import { EvolutionStatusPicker } from './EvolutionStatus'
import { VITALS } from '@/lib/vitals'
import type { EvolutionStatus, StandaloneProgressNoteCreate } from '@/types'

// ─── Labels ──────────────────────────────────────────────────────────────────

const VISIT_TYPE_LABELS: Record<string, string> = {
  routine_control: 'Control rutinario',
  urgent: 'Urgente',
  follow_up: 'Seguimiento',
  first_visit: 'Primera consulta por esta condición',
  other: 'Otro',
}

const PROGRESS_LABELS: Record<string, string> = {
  improved: 'Mejoró',
  unchanged: 'Sin cambios',
  worsened: 'Empeoró',
  new_condition: 'Condición nueva',
}

const ADHERENCE_LABELS: Record<string, string> = {
  full: 'Apego completo',
  partial: 'Apego parcial',
  none: 'No tomó el tratamiento',
  not_applicable: 'No aplica',
}

const ADJUSTMENT_LABELS: Record<string, string> = {
  no_changes: 'Sin cambios',
  new_medication: 'Medicamento nuevo',
  discontinued: 'Medicamento suspendido',
  dosage_change: 'Cambio de dosis',
}

// Sentinel value for "no selection" on optional Selects.
// Radix UI Select throws if SelectItem value is an empty string,
// so we use this non-empty sentinel and treat it as "unset" in the payload.
const NONE = 'none'

const SIGNED_STATUSES = new Set(['completed', 'locked', 'unlock_requested'])

// ─── Section card ─────────────────────────────────────────────────────────────

function SectionCard({
  icon: Icon,
  label,
  open,
  onToggle,
  children,
}: {
  icon: React.ElementType
  label: string
  open: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-ml-outline-variant/20 bg-ml-surface-container-lowest shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-ml-surface-container-low/50"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-ml-primary/10">
          <Icon className="h-3.5 w-3.5 text-ml-primary" />
        </span>
        <span className="flex-1 text-[11px] font-bold uppercase tracking-widest text-ml-primary">
          {label}
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-ml-on-surface-variant/40" />
        ) : (
          <ChevronDown className="h-4 w-4 text-ml-on-surface-variant/40" />
        )}
      </button>

      {open && (
        <div className="border-t border-ml-outline-variant/10 px-4 py-4 space-y-4">
          {children}
        </div>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-widest text-ml-on-surface-variant/70 font-bold">
        {label}
      </Label>
      {children}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

interface NewProgressNoteDialogProps {
  patientId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Optional: pre-loaded latest signed note for context and pre-population. */
  latestNote?: ConsultationSummary | null
}

/**
 * Inline "Registrar nueva nota" sheet that creates a standalone progress note
 * (consultation + walk-in appointment) without leaving the Evolution tab.
 *
 * Blocks: Encabezado · Subjetivo · Objetivo · Valoración · Plan
 * Each block is collapsible for a compact view during a busy visit.
 */
export function NewProgressNoteDialog({
  patientId,
  open,
  onOpenChange,
  latestNote,
}: NewProgressNoteDialogProps) {
  const queryClient = useQueryClient()

  // ─ Section open/closed state ─────────────────────────────────────────────
  const [sections, setSections] = useState({
    header: true,
    subjective: true,
    objective: true,
    assessment: true,
    plan: false,
  })
  const toggleSection = (key: keyof typeof sections) =>
    setSections((s) => ({ ...s, [key]: !s[key] }))

  // ─ Form state ────────────────────────────────────────────────────────────
  const [visitType, setVisitType] = useState('routine_control')
  const [visitDatetime, setVisitDatetime] = useState('')
  const [chiefComplaint, setChiefComplaint] = useState('')

  // Subjective
  const [currentSymptoms, setCurrentSymptoms] = useState('')
  const [progressSince, setProgressSince] = useState('')
  const [treatmentAdherence, setTreatmentAdherence] = useState('')

  // Objective
  const [vitals, setVitals] = useState<Record<string, string>>({})
  const [physicalExam, setPhysicalExam] = useState('')

  // Assessment
  const [diagnosisCodes, setDiagnosisCodes] = useState<string[]>([])
  const [evolutionStatus, setEvolutionStatus] = useState<EvolutionStatus | ''>('')
  const [comparisonNote, setComparisonNote] = useState('')

  // Plan
  const [treatmentAdjustment, setTreatmentAdjustment] = useState('')
  const [treatmentAdjustmentDetail, setTreatmentAdjustmentDetail] = useState('')
  const [studiesOrdered, setStudiesOrdered] = useState('')
  const [nextAppointmentDate, setNextAppointmentDate] = useState('')

  const [isSaving, setIsSaving] = useState(false)

  // ─ Patient context ────────────────────────────────────────────────────────
  // Reuse the parent's query cache (same key) for zero extra network round-trips
  // when the parent has already loaded consultations. Falls back to fetching if
  // the cache is empty.
  const {
    data: consultations,
    isLoading: contextLoading,
    isError: contextError,
    refetch: retryContext,
  } = useQuery({
    queryKey: ['consultations-by-patient', patientId],
    queryFn: () => getAllConsultationsByPatient(patientId),
    enabled: open && !!patientId,
    staleTime: 60_000,
    retry: 1,
  })

  // Resolve the most recent signed consultation: prefer the prop (already in
  // memory from the parent) so the form renders instantly on open.
  const resolvedLatest: ConsultationSummary | null =
    latestNote ??
    (consultations
      ?.filter((c) => SIGNED_STATUSES.has(c.status))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0] ??
      null)

  // ─ Reset form and pre-populate when the sheet opens ──────────────────────
  const prevOpen = useRef(false)
  useEffect(() => {
    if (!open || prevOpen.current) {
      prevOpen.current = open
      return
    }
    prevOpen.current = true

    // Reset section visibility
    setSections({ header: true, subjective: true, objective: true, assessment: true, plan: false })

    // Reset all fields
    setVisitType('routine_control')
    setVisitDatetime('')
    setChiefComplaint('')
    setCurrentSymptoms('')
    setProgressSince('')
    setTreatmentAdherence('')
    setVitals({})
    setPhysicalExam('')
    setEvolutionStatus('')
    setComparisonNote('')
    setTreatmentAdjustment('')
    setTreatmentAdjustmentDetail('')
    setStudiesOrdered('')
    setNextAppointmentDate('')

    // Pre-populate diagnoses from the most recent signed note so the doctor
    // starts with the patient's known conditions rather than a blank list.
    const source = latestNote ?? null
    setDiagnosisCodes(source?.diagnosis_codes?.length ? [...source.diagnosis_codes] : [])
  }, [open]) // intentionally omits latestNote — pre-populate only on open

  // When the sheet closes, reset the prevOpen ref so the next open re-triggers
  useEffect(() => {
    if (!open) prevOpen.current = false
  }, [open])

  // ─ Helpers ───────────────────────────────────────────────────────────────
  const updateVital = (key: string, value: string) => {
    setVitals((prev) => {
      if (value === '') {
        const next = { ...prev }
        delete next[key]
        return next
      }
      return { ...prev, [key]: value }
    })
  }

  const toOptional = (v: string) => (v && v !== NONE ? v : undefined)

  const buildPayload = (signImmediately: boolean): StandaloneProgressNoteCreate => {
    const parsedVitals: Record<string, number> = {}
    for (const [k, v] of Object.entries(vitals)) {
      const n = parseFloat(v)
      if (!isNaN(n)) parsedVitals[k] = n
    }

    const subjectiveParts: string[] = []
    if (currentSymptoms.trim()) subjectiveParts.push(currentSymptoms.trim())
    const ps = toOptional(progressSince)
    if (ps) subjectiveParts.push(`Evolución desde última visita: ${PROGRESS_LABELS[ps] ?? ps}`)
    const ta = toOptional(treatmentAdherence)
    if (ta) subjectiveParts.push(`Apego al tratamiento: ${ADHERENCE_LABELS[ta] ?? ta}`)

    const planParts: string[] = []
    const adj = toOptional(treatmentAdjustment)
    if (adj) {
      const adjLabel = ADJUSTMENT_LABELS[adj] ?? adj
      planParts.push(`Ajuste de tratamiento: ${adjLabel}${treatmentAdjustmentDetail ? ' — ' + treatmentAdjustmentDetail : ''}`)
    }
    if (studiesOrdered.trim()) planParts.push(`Estudios solicitados: ${studiesOrdered.trim()}`)
    if (nextAppointmentDate) planParts.push(`Próxima cita sugerida: ${nextAppointmentDate}`)

    return {
      patient_id: patientId,
      visit_type: visitType,
      visit_datetime: visitDatetime || undefined,
      chief_complaint: chiefComplaint.trim() || undefined,
      subjective_notes: subjectiveParts.join('\n') || undefined,
      objective_notes: physicalExam.trim() || undefined,
      assessment: comparisonNote.trim() || undefined,
      plan_notes: planParts.join('\n') || undefined,
      evolution_status: evolutionStatus || undefined,
      diagnosis_codes: diagnosisCodes.length > 0 ? diagnosisCodes : undefined,
      vitals: Object.keys(parsedVitals).length > 0 ? parsedVitals : undefined,
      progress_since_last_visit: toOptional(progressSince),
      treatment_adherence: toOptional(treatmentAdherence),
      treatment_adjustment: toOptional(treatmentAdjustment),
      treatment_adjustment_detail: treatmentAdjustmentDetail.trim() || undefined,
      studies_ordered: studiesOrdered.trim()
        ? studiesOrdered.split(',').map((s) => s.trim()).filter(Boolean)
        : undefined,
      next_appointment_date: nextAppointmentDate || undefined,
      sign_immediately: signImmediately,
    }
  }

  const canSign =
    chiefComplaint.trim().length > 0 &&
    diagnosisCodes.length > 0 &&
    Object.keys(vitals).length > 0

  const handleSave = async (signImmediately: boolean) => {
    if (signImmediately && !canSign) {
      toast({
        title: 'Campos requeridos para firmar',
        description: 'Se requiere: motivo de consulta, al menos un código CIE-10 y al menos un signo vital.',
        variant: 'destructive',
      })
      return
    }

    setIsSaving(true)
    try {
      await consultationService.createStandalone(buildPayload(signImmediately))
      queryClient.invalidateQueries({ queryKey: ['consultations-by-patient'] })
      queryClient.invalidateQueries({ queryKey: ['clinical-summary'] })
      toast({
        title: signImmediately ? 'Nota firmada y guardada' : 'Borrador guardado',
        description: signImmediately
          ? 'La nota de evolución quedó firmada en el expediente.'
          : 'El borrador se guardó. Puedes retomarlo desde la pestaña de Diagnóstico.',
      })
      onOpenChange(false)
    } catch (err: any) {
      toast({
        title: 'No se pudo guardar la nota',
        description: err?.response?.data?.detail ?? err?.message ?? 'Error inesperado.',
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-3xl p-0 flex flex-col max-h-[90vh] gap-0">
        {/* ── Dialog header ───────────────────────────────────────────── */}
        <DialogHeader className="border-b border-ml-outline-variant/15 px-6 py-5 flex-none">
          <DialogTitle className="flex items-center gap-2 text-xl font-headline italic text-ml-primary">
            <Stethoscope className="h-5 w-5" />
            Nueva nota de evolución
          </DialogTitle>
          <p className="text-xs text-ml-on-surface-variant mt-1">
            Completa los bloques clínicos y guarda como borrador o firma directamente.
          </p>
        </DialogHeader>

        {/* ── Scrollable body ─────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3 min-h-0">

          {/* ── Patient context banner ──────────────────────────────── */}
          {contextLoading && !resolvedLatest && (
            <div className="flex items-center gap-2 rounded-xl border border-ml-outline-variant/15 bg-ml-surface-container-low px-4 py-3 text-sm text-ml-on-surface-variant">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              Cargando historial del paciente…
            </div>
          )}

          {contextError && !resolvedLatest && (
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div className="flex-1 text-sm text-amber-800">
                No se pudo cargar el historial del paciente. Puedes continuar de todas formas.
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => retryContext()}
                className="shrink-0 h-7 px-2 text-xs border-amber-300 text-amber-800 hover:bg-amber-100"
              >
                <RefreshCw className="mr-1 h-3 w-3" />
                Reintentar
              </Button>
            </div>
          )}

          {resolvedLatest && (
            <div className="rounded-xl border border-ml-outline-variant/15 bg-ml-surface-container-low px-4 py-3 space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/60">
                Referencia — última visita
              </p>
              <p className="text-xs text-ml-on-surface-variant">
                {new Date(resolvedLatest.created_at).toLocaleDateString('es-MX', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
                {resolvedLatest.chief_complaint && (
                  <span className="ml-2 text-ml-on-surface">· {resolvedLatest.chief_complaint}</span>
                )}
              </p>
              {resolvedLatest.diagnosis_codes.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {resolvedLatest.diagnosis_codes.map((code) => (
                    <span
                      key={code}
                      className="inline-flex rounded bg-ml-primary-fixed px-2 py-0.5 font-mono text-[10px] font-semibold text-ml-primary"
                    >
                      {code}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Encabezado ────────────────────────────────────────────── */}
          <SectionCard
            icon={CalendarClock}
            label="Encabezado de la visita"
            open={sections.header}
            onToggle={() => toggleSection('header')}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Tipo de visita">
                <Select value={visitType} onValueChange={setVisitType}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(VISIT_TYPE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Fecha y hora de la visita (opcional)">
                <Input
                  type="datetime-local"
                  value={visitDatetime}
                  onChange={(e) => setVisitDatetime(e.target.value)}
                  className="h-9 text-sm"
                />
              </Field>
            </div>

            <Field label="Motivo de consulta *">
              <Input
                value={chiefComplaint}
                onChange={(e) => setChiefComplaint(e.target.value)}
                placeholder="p. ej. Revisión de hipertensión arterial, dolor abdominal 3 días"
                className="h-9 text-sm"
              />
            </Field>
          </SectionCard>

          {/* ── Subjetivo ────────────────────────────────────────────── */}
          <SectionCard
            icon={Activity}
            label="Subjetivo — lo que refiere el paciente"
            open={sections.subjective}
            onToggle={() => toggleSection('subjective')}
          >
            <Field label="Síntomas actuales / motivo detallado">
              <Textarea
                value={currentSymptoms}
                onChange={(e) => setCurrentSymptoms(e.target.value)}
                placeholder="Describe los síntomas, su inicio y características..."
                className="min-h-[80px] resize-y text-sm"
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Evolución desde última visita">
                <Select value={progressSince} onValueChange={setProgressSince}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Selecciona…" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Use 'none' sentinel — Radix UI throws on value="" */}
                    <SelectItem value={NONE}>— Sin especificar —</SelectItem>
                    {Object.entries(PROGRESS_LABELS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Apego al tratamiento previo">
                <Select value={treatmentAdherence} onValueChange={setTreatmentAdherence}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Selecciona…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>— Sin especificar —</SelectItem>
                    {Object.entries(ADHERENCE_LABELS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </SectionCard>

          {/* ── Objetivo ─────────────────────────────────────────────── */}
          <SectionCard
            icon={ClipboardList}
            label="Objetivo — hallazgos del médico"
            open={sections.objective}
            onToggle={() => toggleSection('objective')}
          >
            <div>
              <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/60">
                Signos vitales *
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {VITALS.map((v) => (
                  <div
                    key={v.key}
                    className="rounded-lg border border-ml-outline-variant/15 bg-ml-surface-container-high/30 px-3 py-2.5 space-y-1.5"
                  >
                    <p className="text-[10px] font-semibold text-ml-on-surface-variant/60 uppercase tracking-wider">
                      {v.label}
                    </p>
                    <div className="relative">
                      <Input
                        type="number"
                        inputMode="decimal"
                        value={vitals[v.key] ?? ''}
                        onChange={(e) => updateVital(v.key, e.target.value)}
                        placeholder="—"
                        className="h-8 pr-9 text-sm"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-ml-on-surface-variant/50 pointer-events-none">
                        {v.unit}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Field label="Exploración física relevante">
              <Textarea
                value={physicalExam}
                onChange={(e) => setPhysicalExam(e.target.value)}
                placeholder="Hallazgos relevantes por región o aparato..."
                className="min-h-[80px] resize-y text-sm"
              />
            </Field>
          </SectionCard>

          {/* ── Valoración ───────────────────────────────────────────── */}
          <SectionCard
            icon={Stethoscope}
            label="Valoración — diagnóstico y estado"
            open={sections.assessment}
            onToggle={() => toggleSection('assessment')}
          >
            <Field label="Diagnóstico(s) CIE-10 *">
              <Cie10Combobox
                selectedCodes={diagnosisCodes}
                onCodesChange={setDiagnosisCodes}
              />
            </Field>

            <Field label="Estado del diagnóstico (alimenta el indicador de evolución)">
              <EvolutionStatusPicker
                value={evolutionStatus}
                onChange={setEvolutionStatus}
              />
            </Field>

            <Field label="Comparación con visita previa (opcional)">
              <Textarea
                value={comparisonNote}
                onChange={(e) => setComparisonNote(e.target.value)}
                placeholder="p. ej. Buen control, sin cambios en medicación..."
                className="min-h-[60px] resize-y text-sm"
              />
            </Field>
          </SectionCard>

          {/* ── Plan ─────────────────────────────────────────────────── */}
          <SectionCard
            icon={FlaskConical}
            label="Plan — tratamiento, estudios y seguimiento"
            open={sections.plan}
            onToggle={() => toggleSection('plan')}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Ajuste de tratamiento">
                <Select value={treatmentAdjustment} onValueChange={setTreatmentAdjustment}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Selecciona…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>— Sin especificar —</SelectItem>
                    {Object.entries(ADJUSTMENT_LABELS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {['new_medication', 'discontinued', 'dosage_change'].includes(treatmentAdjustment) && (
                <Field label="Detalle del ajuste">
                  <Input
                    value={treatmentAdjustmentDetail}
                    onChange={(e) => setTreatmentAdjustmentDetail(e.target.value)}
                    placeholder="p. ej. Metformina 850 mg c/12h"
                    className="h-9 text-sm"
                  />
                </Field>
              )}
            </div>

            <Field label="Estudios solicitados (separados por comas)">
              <Input
                value={studiesOrdered}
                onChange={(e) => setStudiesOrdered(e.target.value)}
                placeholder="p. ej. Hemograma, Glucosa, Perfil lipídico"
                className="h-9 text-sm"
              />
            </Field>

            <Field label="Próxima cita sugerida">
              <div className="flex items-center gap-2">
                <CalendarClock className="h-4 w-4 text-ml-on-surface-variant/50 shrink-0" />
                <Input
                  type="date"
                  value={nextAppointmentDate}
                  onChange={(e) => setNextAppointmentDate(e.target.value)}
                  className="h-9 text-sm flex-1"
                />
              </div>
            </Field>
          </SectionCard>

          <div className="h-1" />
        </div>

        {/* ── Footer actions ───────────────────────────────────────────── */}
        <div className="border-t border-ml-outline-variant/15 bg-ml-surface px-6 py-4 flex-none">
          {!canSign && (
            <p className="mb-3 text-[11px] text-ml-on-surface-variant/60">
              Para firmar se requiere: motivo de consulta, al menos un código CIE-10 y al menos un signo vital.
            </p>
          )}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
              className="sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSave(false)}
              disabled={isSaving || !chiefComplaint.trim()}
              className="sm:w-auto"
            >
              {isSaving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Guardar borrador
            </Button>
            <Button
              onClick={() => handleSave(true)}
              disabled={isSaving || !canSign}
              className={cn('sm:w-auto', !canSign && 'opacity-50 cursor-not-allowed')}
            >
              {isSaving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileSignature className="mr-2 h-4 w-4" />
              )}
              Firmar nota
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
